import { Component } from '@angular/core';
import { Subscription, takeUntil } from 'rxjs';
import { View } from 'src/graphics/view';
import { Store } from 'src/sync/store';
import { WatchAwareComponent } from '../watch-aware.component';
import { Token } from 'src/graphics/token';
import { Room } from 'src/sync/room';
import { Util } from 'src/util/util';
import { Mouse } from 'src/graphics/mouse';
import { Settings } from 'src/graphics/settings';
import { Gfx } from 'src/graphics/gfx';

@Component({
    selector: 'app-token',
    templateUrl: './token.component.html',
    styleUrls: ['./token.component.scss'],
    standalone: false
})
export class TokenComponent extends WatchAwareComponent {
    public token: Token | undefined;
    private tokensub?: Subscription;

    public ngOnInit() {
        Store.activeSprite.pipe(takeUntil(this.destroyed)).subscribe((sprite) => {
            if (sprite == null || sprite.type != 'token') {
                this.token = undefined;
                this.changeDetectorRef.detectChanges();
            } else {
                this.token = sprite as Token;
                this.changeDetectorRef.detectChanges();
                this.open(true);
                this.tokensub?.unsubscribe();
                this.tokensub = this.token.moved.subscribe(() => this.open());
            }
        });

        const tryOpen = () => { if (this.token != null) this.open() }
        window.addEventListener('pointermove', () => { if (Mouse.panning) tryOpen(); });
        window.addEventListener('resize', () => setTimeout(tryOpen, 50));
        window.addEventListener('wheel', () => setTimeout(tryOpen, 50));
    }

    public open(fresh = false) {
        if (this.token == null) return;
        const element = document.querySelector('#token-editor') as HTMLElement;
        const panel = element.querySelector('#token-panel') as HTMLElement;
        const input = panel.querySelector('input') as HTMLInputElement;
        const rotator = element.querySelector('#token-rotate') as HTMLElement;
        const resizeLeft = element.querySelector('.token-resize') as HTMLElement;

        const position = this.token.sprite.getGlobalPosition();
        const scale = View.viewport.scale.x;
        let leftOffset = (panel.clientWidth / 2);
        let topOffset = (this.token.sprite.height / 2) * scale * 1.1;

        panel.style.left = `${position.x - leftOffset}px`;
        panel.style.top = `${position.y + topOffset}px`;

        leftOffset = -(rotator.clientWidth / 2);
        topOffset = (this.token.sprite.height / 2) * scale * 1.2;
        rotator.style.left = `${position.x + leftOffset}px`;
        rotator.style.top = `${(position.y - topOffset - 50)}px`;

        leftOffset = -(this.token.sprite.width / 2) * scale;
        topOffset = -(resizeLeft.clientHeight / 2);
        resizeLeft.style.left = `${position.x + leftOffset - resizeLeft.clientWidth}px`;
        resizeLeft.style.top = `${position.y + topOffset}px`;

        if (fresh) {
            input.focus();
            setTimeout(() => input.select());
        }
    }

    public keydown(e: KeyboardEvent) {
        if (e.code == 'Escape' || Util.isAffirmativeKey(e)) {
            Store.activeSprite.value?.deselect();
        }
        e.stopPropagation();
    }

    public rotate(e: PointerEvent) {
        const rotator = document.getElementById('token-rotate') as HTMLElement;
        if (this.token == null || rotator.hasPointerCapture(e.pointerId) == false) return;
        const center = this.token.sprite.position.clone();
        const mouse = View.viewport.toWorld(e.clientX, e.clientY);
        let rotation = Util.getRadianAngle(center, mouse);
        const snapfactor = e.shiftKey ? Math.PI / 24 : Math.PI / 8;
        rotation = Math.round(rotation / snapfactor) * snapfactor;

        if (this.token.sprite.rotation != rotation) {
            Room.tokens.modify(this.token.data.id, x => x.rotation = rotation);
        }
    }

    public beginRotate(e: PointerEvent) {
        const rotator = document.getElementById('token-rotate') as HTMLElement;
        rotator.style.cursor = 'grabbing';
        rotator.setPointerCapture(e.pointerId);
        Gfx.mouse.clear();
    }

    public endRotate(e: PointerEvent) {
        const rotator = document.getElementById('token-rotate') as HTMLElement;
        rotator.style.cursor = 'grab';
        rotator.releasePointerCapture(e.pointerId);
        Mouse.draw();
    }

    public resize(e: PointerEvent) {
        const resizeLeft = document.querySelector('.token-resize') as HTMLElement;
        if (this.token == null || resizeLeft.hasPointerCapture(e.pointerId) == false) return;
        const center = this.token.sprite.position.clone();
        const mouse = View.viewport.toWorld(e.clientX, e.clientY);
        const distance = center.x - mouse.x;

        let factor = 1;
        const ratio = distance / (Settings.gridOffset / 2);
        if (ratio < 1) {
            const floors = [1, 1 / 2, 1 / 3, 1 / 4, 1 / 5];
            factor = Math.max(...floors.filter(x => x <= ratio));
        } else {
            factor = ratio;
            if (e.shiftKey == false)
                factor = 1 * Math.floor(factor);
        }

        if (this.token.data.scale.x != factor) {
            Room.tokens.modify(this.token.data.id, x => x.scale = { x: factor, y: factor });
            this.open();
        }
    }

    public beginResize(e: PointerEvent) {
        const resizer = document.querySelector('.token-resize') as HTMLElement;
        resizer.setPointerCapture(e.pointerId);
        Gfx.mouse.clear();
    }

    public endResize(e: PointerEvent) {
        const resizer = document.querySelector('.token-resize') as HTMLElement;
        resizer.releasePointerCapture(e.pointerId);
        Mouse.draw();
    }

    public copy(token: Token) {
        Mouse.pasteTarget = { token: token.data, offsetX: token.sprite.width / 2, offsetY: token.sprite.height / 2 };
        navigator.clipboard.writeText('');
        token.deselect();
    }

    public update(token: Token) {
        Room.tokens.set(token.data.id, token.data);
    }

    public toggleVisibility(token: Token) {
        token.data.visible = !token.data.visible;
        Room.tokens.modify(token.data.id, x => x.visible = !x.visible);
        this.changeDetectorRef.detectChanges();
    }
}
